import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowUpDown } from "lucide-react";
import { MovementTypeBadge } from "./MovementTypeBadge";
import { ReferenceLink } from "./ReferenceLink";
import { TablePagination } from "../../leave-management/components/TablePagination";

const MONTH_KEYS = [
  "dateTime.monthJan",
  "dateTime.monthFeb",
  "dateTime.monthMar",
  "dateTime.monthApr",
  "dateTime.monthMay",
  "dateTime.monthJun",
  "dateTime.monthJul",
  "dateTime.monthAug",
  "dateTime.monthSep",
  "dateTime.monthOct",
  "dateTime.monthNov",
  "dateTime.monthDec",
];

export function InventoryJournalTable({
  data,
  page,
  pageSize,
  onPageChange,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Paginated list
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, page, pageSize]);

  // Format date helper
  const formatDate = (isoStr) => {
    if (!isoStr) return "—";
    const dateObj = new Date(isoStr);
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = t(MONTH_KEYS[dateObj.getMonth()]);
    const year = dateObj.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleRowClick = (row) => {
    let mockId = "1";
    const ref = row.reference || "";
    if (ref.includes("ALC-0028") || ref.includes("8899")) {
      mockId = "2";
    } else if (ref.includes("ALC-0025") || ref.includes("0112")) {
      mockId = "3";
    } else if (ref.includes("ALC-0026") || ref.includes("7756")) {
      mockId = "4";
    }
    navigate(`/inventory-management/allocation-detail/${mockId}`);
  };

  return (
    <div className="space-y-4 font-sans">
      <div className="overflow-x-auto rounded-xl border border-[var(--color-text-border)] bg-white shadow-sm">
        <table className="w-full text-sm table-fixed min-w-[1100px] border-collapse">
          <colgroup>
            <col className="w-[12%]" />
            <col className="w-[13%]" />
            <col className="w-[20%]" />
            <col className="w-[17%]" />
            <col className="w-[10%]" />
            <col className="w-[7%]" />
            <col className="w-[8%]" />
            <col className="w-[8%]" />
            <col className="w-[10%]" />
          </colgroup>
          <thead className="sticky top-0 z-10">
            <tr className="text-[10px] uppercase tracking-wider text-[var(--color-text-secondary)] bg-[var(--color-workspace)]/30 border-b border-[var(--color-text-border)]/60">
              <th className="px-4 py-3 font-bold text-left cursor-pointer hover:text-foreground transition-colors">
                <div className="flex items-center gap-1">
                  <span>{t("dateTime.date")}</span>
                  <ArrowUpDown className="h-3 w-3" />
                </div>
              </th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.movement")}</th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.product")}</th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.warehouse")}</th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.batch")}</th>
              <th className="px-4 py-3 font-bold text-right">{t("inventory.qty")}</th>
              <th className="px-4 py-3 font-bold text-right">{t("inventory.balance")}</th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.reference")}</th>
              <th className="px-4 py-3 font-bold text-left">{t("inventory.recordedBy")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--color-text-border)]/35">
            {pagedRows.map((row) => {
              const isPositive = row.quantity > 0;
              return (
                <tr
                  key={row.id}
                  onClick={() => handleRowClick(row)}
                  className="hover:bg-[var(--color-workspace)]/20 transition-colors cursor-pointer"
                >
                  {/* Date */}
                  <td className="px-4 py-3.5 text-[var(--color-text-secondary)] font-medium">
                    {formatDate(row.date)}
                  </td>

                  {/* Movement Status Badge */}
                  <td className="px-4 py-3.5">
                    <MovementTypeBadge type={row.movementType} />
                  </td>

                  {/* Product & Part Number */}
                  <td className="px-4 py-3.5">
                    <span className="font-bold text-foreground block truncate leading-tight">
                      {row.productName}
                    </span>
                    <span className="text-[11px] font-semibold text-[var(--color-text-secondary)] block mt-0.5 font-mono">
                      {row.partNumber}
                    </span>
                  </td>

                  {/* Warehouse */}
                  <td className="px-4 py-3.5 text-[var(--color-text-secondary)] font-medium truncate">
                    {row.warehouse}
                  </td>

                  {/* Batch Number */}
                  <td className="px-4 py-3.5 text-[var(--color-text-secondary)] font-mono font-medium truncate">
                    {row.batchNumber}
                  </td>

                  {/* Quantity with + / - signs */}
                  <td
                    className={cn(
                      "px-4 py-3.5 text-right font-extrabold font-mono text-xs",
                      isPositive ? "text-emerald-600" : "text-rose-600"
                    )}
                  >
                    {isPositive ? `+${row.quantity}` : row.quantity}
                  </td>

                  {/* Running Balance */}
                  <td className="px-4 py-3.5 text-right font-bold text-foreground">
                    {row.runningBalance.toLocaleString()}
                  </td>

                  {/* Reference click log document link */}
                  <td className="px-4 py-3.5">
                    <ReferenceLink value={row.reference} />
                  </td>

                  {/* Recorded Employee */}
                  <td className="px-4 py-3.5 text-[var(--color-text-secondary)] font-medium truncate">
                    {row.recordedBy}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination component */}
      <TablePagination
        page={page}
        totalPages={totalPages}
        totalItems={data.length}
        pageSize={pageSize}
        onPageChange={onPageChange}
      />
    </div>
  );
}

// Inline helper for CN class formatting in case of import conflicts
function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}
